import React, { useState, useCallback } from "react";
import { Box, Typography, Stepper, Step, StepLabel, Button } from "@mui/material";
import { useLocation } from "react-router-dom";
import Topbar from "../Topbar";
import CSVUploadStep from "./steps/bulk/CSVUploadStep";
import BulkProjectSizeStep from "./steps/bulk/BulkProjectSizeStep";
import BulkDataSourcesStep from "./steps/bulk/BulkDataSourcesStep";
import BulkReviewStep from "./steps/bulk/BulkReviewStep";
import { BulkCreateProjectData } from "./types";

const steps = [
  "Upload CSV",
  "Project Size",
  "Data Sources",
  "Review & Create",
];

const BulkCreateProjectWizard: React.FC = () => {
  // The listing passes the folder it was opened from; every project created
  // here is filed there.
  const { state } = useLocation();
  const presetFolderId = (state as { folderId?: number | null } | null)?.folderId ?? null;
  const [projectData, setProjectData] = useState<BulkCreateProjectData>({
    folderId: presetFolderId,
    csvFile: null,
    parsedProjects: [],
    projectSize: "small",
    projectAspectRatio: "1:1",
    vegetationSource: "",
    fireHistorySource: "",
    selectedAssetSources: [],
    selectedTreatmentSources: [],
    selectedPropertySources: [],
    stampRoadsIntoVegetation: true,
    roadSource: "",
    modelUrbanEnvironment: false,
    defaultConstructionTypeId: "",
  });

  const [activeStep, setActiveStep] = useState<number>(0);

  const canProceed = useCallback(
    (step: number): boolean => {
      switch (step) {
        case 0:
          return (
            projectData.csvFile !== null &&
            projectData.parsedProjects.length > 0
          );
        case 1:
          return projectData.parsedProjects.length > 0;
        case 2:
          return (
            projectData.vegetationSource !== "" &&
            projectData.fireHistorySource !== "" &&
            // Urban modelling requires a default construction type (#203)
            (!projectData.modelUrbanEnvironment ||
              projectData.defaultConstructionTypeId !== "")
          );
        case 3:
          return true; // Review step
        default:
          return true;
      }
    }, [projectData]);

  const handleNext = () => {
    setActiveStep((prevStep: number) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep: number) => prevStep - 1);
  };

  const updateProjectData = (updates: Partial<BulkCreateProjectData>) => {
    setProjectData((prev: BulkCreateProjectData) => ({ ...prev, ...updates }));
  };

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <CSVUploadStep
            data={projectData}
            onNext={handleNext}
            onUpdateData={updateProjectData}
          />
        );
      case 1:
        return (
          <BulkProjectSizeStep
            data={projectData}
            onNext={handleNext}
            onBack={handleBack}
            onUpdateData={updateProjectData}
          />
        );
      case 2:
        return (
          <BulkDataSourcesStep
            data={projectData}
            onNext={handleNext}
            onBack={handleBack}
            onUpdateData={updateProjectData}
          />
        );
      case 3:
        return (
          <BulkReviewStep
            data={projectData}
            onBack={handleBack}
            onUpdateData={updateProjectData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box sx={{ height: "var(--vh100)", display: "flex", flexDirection: "column" }}>
      {/* Topbar Navigation */}
      <Topbar />

      {/* Header */}
      <Box
        sx={{
          backgroundColor: "white",
          p: 3,
          borderBottom: "1px solid #e0e0e0",
          zIndex: 1000,
        }}
      >
        <Typography
          variant="h4"
          sx={{
            mb: 2,
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Create Projects in Bulk
        </Typography>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label, index) => (
            <Step key={label} completed={index < activeStep}>
              <StepLabel
                sx={{
                  "& .MuiStepIcon-root": {
                    color: index <= activeStep ? "#00d4ad" : "#e0e0e0",
                    "&.Mui-completed": {
                      color: "#00d4ad",
                    },
                    "&.Mui-active": {
                      color: "#00d4ad",
                    },
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: index <= activeStep ? "#1a1a1a" : "#9e9e9e",
                    fontWeight: index === activeStep ? 600 : 400,
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
        <Typography variant="body2" color="text.secondary">
          {`Step ${activeStep + 1} of ${steps.length}`}
          {projectData.parsedProjects.length > 0 && (
            <Typography component="span" sx={{ ml: 2, color: "#00d4ad", fontWeight: 600 }}>
              • {projectData.parsedProjects.length} projects loaded
            </Typography>
          )}
        </Typography>
      </Box>

      {/* Step Content */}
      <Box sx={{ flex: 1, overflow: "auto", minHeight: 0 }}>
        {renderStepContent(activeStep)}
      </Box>

      {/* Global Bottom Navigation */}
      {activeStep < steps.length - 1 && (
        <Box
          sx={{
            p: 3,
            backgroundColor: "#f8f9fa",
            borderTop: "1px solid #e0e0e0",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "sticky",
            bottom: 0,
            zIndex: 1000,
          }}
        >
          <Button
            variant="outlined"
            onClick={handleBack}
            disabled={activeStep === 0}
            sx={{
              borderColor: "#e0e0e0",
              color: "#666",
              "&:hover": {
                backgroundColor: "#f5f5f5",
                borderColor: "#ccc",
              },
            }}
          >
            Previous
          </Button>

          {activeStep < steps.length - 1 && (
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={!canProceed(activeStep)}
              sx={{
                backgroundColor: "#00d4ad",
                "&:hover": { backgroundColor: "#00b89c" },
                "&:disabled": { backgroundColor: "#e0e0e0", color: "#999" },
              }}
            >
              {activeStep === 0 && projectData.parsedProjects.length > 0
                ? `Next (${projectData.parsedProjects.length} projects)`
                : "Next"}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BulkCreateProjectWizard;
